/* eslint-disable @typescript-eslint/naming-convention */
import type { EventTournament } from '@ps/web-martech-cms-types';
import type { SelectOption } from '@ps/web-shared-components';
import { DropDown, RawButton } from '@ps/web-shared-components';
import { Label } from '@ps/web-shared-ui';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { FormattedNumber } from 'react-intl';

import LoadingIndicator from '@swf/components/LoadingIndicator/LoadingIndicator';
import { fetchJson } from '@swf/core/utils/api';

import PanelItem from '../CMSPanel/PanelItem';

import styles from './CMSEventTournament.scss';
import Clock from './components/Clock';
import type { Payouts } from './components/PayoutsTable';
import PayoutsTable from './components/PayoutsTable';
import type { SeatDraw } from './components/SeatDrawTable';
import SeatDrawTable from './components/SeatDrawTable';

interface Level {
  smallBlind: number;
  bigBlind: number;
  ante: number;
  minutes: number;
  index: number;
  type: number;
  id: number;
}

interface SeatDrawData {
  position: string;
  player: { lastname: string; firstname: string };
  tableIndex: string;
  seatIndex: string;
  chipsCount: number;
}

interface PayoutsData {
  position: string;
  amount: number;
}

interface EventsData {
  id: string;
  dailyDetails: {
    name: string;
    date: string;
  };
  stats: {
    totalChipsCount: number;
    averageChipsCount: number;
    totalPlayers: number;
    totalEntries: number;
  };
  status: {
    level: { id: number; elapsedSeconds: number };
  };
  levels: Level[];
  players: SeatDrawData[];
  payouts: PayoutsData[];
}

interface Event {
  title: string;
  startDate: Date;
  entries: string;
  level?: number;
  nextLevel: number;
  blinds?: string;
  nextBlinds?: string;
  averageChips: number;
  totalChips: number;
  next?: string;
  nextBreak?: number;
  elapsedTime?: number;
  levelTime: number;
  seatDraw: SeatDraw[];
  payouts: Payouts[];
}

const GetEvent = 'event/';
const EventSearch = 'event/search';

const getCurrentLevelTime = (data: EventsData) => {
  const endIndex = data.status.level.id ? data.status.level.id - 1 : 0;
  return data.levels[endIndex].minutes * 60 - data.status.level.elapsedSeconds;
};

const getElapsedTime = (data: EventsData) => {
  let levelsElapsedTime = 0;
  for (let i = 0; i < data.status.level.id - 1; i++) {
    const level = data.levels[i];
    if (level) {
      levelsElapsedTime += level.minutes * 60;
    }
  }

  return (levelsElapsedTime + data.status.level.elapsedSeconds) * 1000;
};

const getNextBreakTime = (data: EventsData) => {
  let nextBreakTime = 0;
  const startIndex = data.status.level.id ? data.status.level.id - 1 : 0;
  for (let i = startIndex; i < data.levels.length; i++) {
    const level = data.levels[i];
    if (level != null) {
      nextBreakTime = level.minutes * 60;
    }
  }

  return (nextBreakTime + getCurrentLevelTime(data)) * 1000;
};

const CMSEventTournament: React.FC<EventTournament> = ({ venue_id, api_token, api_url }) => {
  const [eventList, setEventList] = useState<SelectOption[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<SelectOption>();
  const [eventData, setEventData] = useState<Event>();
  const [seatDrawExpanded, setSeatDrawExpanded] = useState(false);
  const [payoutsExpanded, setPayoutsExpanded] = useState(false);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const refreshTriggerInterval = setInterval(() => {
      if (!loading) {
        setTriggerRefresh((trigger) => !trigger);
      }
    }, 60 * 1000);

    return () => clearInterval(refreshTriggerInterval);
  });

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    const fetchData = async () => {
      if (!selectedEvent) {
        return;
      }

      setLoading(true);

      try {
        const data = await fetchJson<EventsData>(api_url + GetEvent + selectedEvent?.value, {
          signal,
          headers: {
            'Content-Type': 'application/json',
            access_token: api_token
          }
        });

        const title = data.dailyDetails?.name;
        const startDate = new Date(data.dailyDetails?.date);
        const entries = `${data.stats.totalPlayers}'/'${data.stats.totalEntries}`;
        const averageChips = data.stats.averageChipsCount;
        const totalChips = data.stats.totalChipsCount;
        const levelData = data.status.level.id
          ? data.levels[data.status.level.id - 1]
          : data.levels[0];
        const nextLevelData = data.levels[data.status.level.id];
        const level = levelData.id;
        const blinds = `${levelData.smallBlind}/${levelData.bigBlind}(${levelData.ante})`;
        const nextLevel = nextLevelData.id;
        const nextBlinds = `${nextLevelData.smallBlind}/${nextLevelData.bigBlind}(${nextLevelData.ante})`;
        const elapsedTime = getElapsedTime(data);
        const nextBreak = getNextBreakTime(data);
        const levelTime = getCurrentLevelTime(data) * 1000;
        const seatDraw = data.players.map((item) => ({
          position: item.position,
          name: `${item.player.lastname} ${item.player.firstname}`,
          seat: `${item.tableIndex} - ${item.seatIndex}`,
          chips: item.chipsCount ? item.chipsCount : ''
        })) as SeatDraw[];
        const payouts = data.payouts.map((item) => ({
          position: item.position,
          prize: item.amount
        })) as Payouts[];

        setEventData({
          title,
          startDate,
          entries,
          averageChips,
          totalChips,
          level,
          blinds,
          nextLevel,
          nextBlinds,
          nextBreak,
          elapsedTime,
          levelTime,
          seatDraw,
          payouts
        });
        setLoading(false);
      } catch {
        setLoading(false);
        setEventData(undefined);
      }
    };

    fetchData();

    return () => abortController.abort();
  }, [api_token, api_url, selectedEvent, triggerRefresh]);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    const fetchData = async () => {
      try {
        const { results } = await fetchJson(api_url + EventSearch, {
          signal,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            access_token: api_token
          },
          body: JSON.stringify({
            includeSummary: false,
            venueId: venue_id
          })
        });

        const events = (results as EventsData[]).map((item: EventsData) => ({
          text: item.dailyDetails.name,
          value: item.id
        })) as SelectOption[];

        setEventList(events);
        setSelectedEvent(events[0]);
      } catch {
        setEventList([]);
      }
    };

    fetchData();

    return () => abortController.abort();
  }, [api_token, api_url, venue_id]);

  return (
    <>
      <div className={styles.actions}>
        <div>
          <Label id="tour-filter">Events</Label>
          <DropDown
            options={eventList}
            id="tour-filter"
            value={selectedEvent?.value}
            onSelect={(event) => setSelectedEvent(event)}
          />
        </div>
        <div className={styles.button}>
          <RawButton variant="primary" onClick={() => setTriggerRefresh((trigger) => !trigger)}>
            Refresh
          </RawButton>
        </div>
      </div>
      {!loading ? (
        <>
          <Clock
            levelTime={eventData?.levelTime || 0}
            nextBreakTime={eventData?.nextBreak || 0}
            elapsedTime={eventData?.elapsedTime || 0}
            onLevelTimeExpired={() => !loading && setTriggerRefresh((trigger) => !trigger)}
          />
          <div className={styles.layout}>
            <div className={styles.layoutItem}>
              <div className={styles.layoutLabel}>Event Date</div>
              <div>{eventData?.startDate && format(eventData?.startDate, 'dd MM yyyy')}</div>
            </div>
            <div className={styles.layoutItem}>
              <div className={styles.layoutLabel}>Entries</div>
              <div>{eventData?.entries}</div>
            </div>
            <div className={styles.layoutItem}>
              <div className={styles.layoutLabel}>Average Chips</div>
              <div>
                {eventData?.averageChips && (
                  <FormattedNumber style="decimal" value={eventData.averageChips} />
                )}
              </div>
            </div>
            <div className={styles.layoutItem}>
              <div className={styles.layoutLabel}>Total Chips</div>
              <div>
                {eventData?.totalChips && (
                  <FormattedNumber style="decimal" value={eventData.totalChips} />
                )}
              </div>
            </div>
            <div className={styles.layoutItem}>
              <div className={styles.layoutLabel}>Level</div>
              <div className={styles.green}>{eventData?.level}</div>
            </div>
            <div className={styles.layoutItem}>
              <div className={styles.layoutLabel}>Blinds</div>
              <div className={styles.green}>{eventData?.blinds}</div>
            </div>
            <div className={styles.layoutItem}>
              <div className={styles.layoutLabel}>Next</div>
              <div className={styles.yellow}>{eventData?.nextLevel}</div>
            </div>
            <div className={styles.layoutItem}>
              <div className={styles.layoutLabel}>Next Blinds</div>
              <div className={styles.yellow}>{eventData?.nextBlinds}</div>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.loadingIndicator}>
          <LoadingIndicator loading={loading} />
        </div>
      )}
      <div className={styles.panel}>
        <PanelItem
          body={[]}
          expanded={seatDrawExpanded}
          expandedTitle="Players"
          collapsedTitle="Players"
          onClick={() => setSeatDrawExpanded((expanded) => !expanded)}
          element={<SeatDrawTable seatDraw={eventData?.seatDraw || []} />}
        />
      </div>
      <div className={styles.panel}>
        <PanelItem
          body={[]}
          expanded={payoutsExpanded}
          expandedTitle="Payouts"
          collapsedTitle="Payouts"
          onClick={() => setPayoutsExpanded((expanded) => !expanded)}
          element={<PayoutsTable payouts={eventData?.payouts || []} />}
        />
      </div>
    </>
  );
};

export default CMSEventTournament;
